import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Grid,
  Alert,
} from '@mui/material';
import {
  Add,
  Remove,
  Delete,
  ArrowBack,
  Payment,
  ShoppingCart,
} from '@mui/icons-material';
import { CartItem } from '../types/CartItem';
import { ontarioTechStyles } from '../theme/ontarioTechTheme';
import { UI_TEXT } from '../constants/appConstants';
import { formatPrice } from '../utils/formatters';
import { priceCart, PriceCartResponse } from '../services/pricingService';
import { productService } from '../services/productService';

interface CartInterfaceProps {
  cartItems: CartItem[];
  totalAmount: number;
  onUpdateItem: (id: string, quantity: number) => void;
  onProceedToPayment: () => void;
  onBackToScanning: () => void;

  locationId?: number;
}

const CartInterface: React.FC<CartInterfaceProps> = ({
  cartItems,
  totalAmount,
  onUpdateItem,
  onProceedToPayment,
  onBackToScanning,
  locationId,
}) => {
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<string, boolean>>({});
  const [autoPricing, setAutoPricing] = useState<PriceCartResponse | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [productIndex, setProductIndex] = useState<Map<string, any> | null>(null);
  console.log('Rendering CartInterface with cartItems:', cartItems);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const products = await productService.getAll();
        const idx = new Map<string, any>();
        products.forEach(p => {
          if (p.barcode) idx.set(p.barcode, p);
          if (p.isbn) idx.set(p.isbn, p);
        });
        if (alive) setProductIndex(idx);
      } catch {
        if (alive) setProductIndex(null);
      }
    })();

    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setPricingError(null);

    if (!cartItems?.length) {
      setAutoPricing(null);
      return;
    }

    (async () => {
      try {
        const res = await priceCart({ cartItems, locationId, channel: 'POS' });
        if (alive) setAutoPricing(res);
      } catch (e: any) {
        if (alive) {
          setAutoPricing(null);
          setPricingError(e?.message || 'Failed to calculate pricing.');
        }
      }
    })();

    return () => { alive = false; };
  }, [cartItems, locationId]);

  const pricingByUpc = useMemo(() => {
    const m = new Map<string, { line_subtotal: number; line_discount: number; applied_promos: string[] }>();
    (autoPricing?.items || []).forEach(i => {
      m.set(i.item_sku, {
        line_subtotal: i.line_subtotal ?? 0,
        line_discount: i.line_discount ?? 0,
        applied_promos: Array.isArray(i.applied_promos) ? i.applied_promos : [],
      });
    });
    return m;
  }, [autoPricing]);

  const appliedPromos = useMemo(() => {
    const s = new Set<string>();
    (autoPricing?.items || []).forEach(i => (i.applied_promos || []).forEach(p => s.add(String(p).toUpperCase())));
    return Array.from(s);
  }, [autoPricing]);

  const totals = useMemo(() => {
    const subtotalBefore = autoPricing?.totals?.subtotal_before_discount ?? totalAmount;
    const discount = autoPricing?.totals?.total_discount ?? 0;
    const subtotalAfter = autoPricing?.totals?.subtotal_after_discount ?? Math.max(0, subtotalBefore - discount);

    let bottleDepositTotal = 0;
    let taxTotal = 0;

    for (const ci of cartItems || []) {
      const upc = (ci.barcode?.trim() || ci.isbn?.trim() || '');
      const qty = Math.max(1, ci.quantity || 1);

      const meta = productIndex?.get(upc);
      const depositPer = Number(meta?.bottleDeposit ?? ci.bottleDeposit ?? 0);
      const depositApplicable = Boolean(meta?.bottleDepositApplicable ?? ci.bottleDepositApplicable);
      if (depositApplicable && depositPer > 0) bottleDepositTotal += depositPer * qty;

      const taxApplicable = Boolean(meta?.taxApplicable ?? ci.taxApplicable);
      const taxRate = Number(meta?.taxRate ?? ci.taxRate ?? 0);

      if (taxApplicable && taxRate > 0) {
        const line = pricingByUpc.get(upc);
        const base = Math.max(
          0,
          (line?.line_subtotal ?? (ci.price * qty)) - (line?.line_discount ?? 0)
        );
        taxTotal += base * taxRate;
      }
    }

    const grandTotal = subtotalAfter + bottleDepositTotal + taxTotal;

    return {
      subtotalBefore,
      discount,
      subtotalAfter,
      bottleDepositTotal,
      taxTotal,
      grandTotal,
    };
  }, [autoPricing, cartItems, pricingByUpc, productIndex, totalAmount]);

  const handleImageLoad = (itemId: string) => {
    setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
  };

  const handleImageError = (itemId: string) => {
    setImageLoadingStates(prev => ({ ...prev, [itemId]: false }));
  };
  const handleQuantityChange = (id: string, currentQuantity: number, delta: number) => {
    const newQuantity = currentQuantity + delta;
    onUpdateItem(id, newQuantity);
  };

  const handleRemoveItem = (id: string) => {
    onUpdateItem(id, 0);
  };


  const getTotalItems = () => {
    return cartItems.reduce((sum: number, item: CartItem) => sum + item.quantity, 0);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={onBackToScanning} size="large" sx={ontarioTechStyles.contrast.whiteIconButton}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h3" sx={{ flexGrow: 1, ...ontarioTechStyles.contrast.whiteText }}>
          {UI_TEXT.CART.TITLE}
        </Typography>
        <Chip icon={<ShoppingCart />} label={`${getTotalItems()} items`} sx={ontarioTechStyles.contrast.whiteChip} />
      </Box>

      {/* Cart Items */}
      <Card sx={{ flex: 1, overflow: 'visible' }}>
        <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {cartItems.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4 }}>
              <ShoppingCart sx={{ fontSize: 80, color: '#F06C38', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#2C507A', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Your cart is empty
              </Typography>
              <Typography sx={{ color: '#6C757D', mb: 3, fontSize: '1rem' }}>
                Start scanning items to add them to your cart
              </Typography>
              <Button
                variant="contained"
                onClick={onBackToScanning}
                size="large"
                sx={{ backgroundColor: '#2C507A', '&:hover': { backgroundColor: '#1E3A5F' } }}
              >
                Start Scanning
              </Button>
            </Box>
          ) : (
            <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
              {cartItems.map((item: CartItem, index: number) => (
                <React.Fragment key={item.id}>
                  <ListItem sx={{ py: 2, px: 3 }}>
                    <Avatar
                      src={item.imageUrl}
                      sx={{
                        width: 60,
                        height: 60,
                        mr: 2,
                        opacity: imageLoadingStates[item.id] ? 0.7 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                      }}
                      variant="rounded"
                      imgProps={{
                        loading: 'lazy',
                        onLoad: () => handleImageLoad(item.id),
                        onError: () => handleImageError(item.id),
                      }}
                    >
                      {item.name.charAt(0)}
                    </Avatar>

                    <ListItemText
                      primary={<Typography variant="h6" noWrap>{item.name}</Typography>}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.author && `by ${item.author}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.category}
                          </Typography>
                          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                            {formatPrice(item.price)} each
                          </Typography>

                          {/* Auto discount per item (if present) */}
                          {(() => {
                            const upc = (item.barcode?.trim() || item.isbn?.trim() || '');
                            const line = pricingByUpc.get(upc);
                            const d = line?.line_discount ?? 0;
                            return d > 0 ? (
                              <Typography variant="body2" sx={{ color: '#28a745', mt: 0.5 }}>
                                Auto Discount: -{formatPrice(d)}
                              </Typography>
                            ) : null;
                          })()}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />

                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => handleQuantityChange(item.id, item.quantity, -1)} disabled={item.quantity <= 1} size="small">
                          <Remove />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center', px: 1, py: 0.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                          {item.quantity}
                        </Typography>
                        <IconButton onClick={() => handleQuantityChange(item.id, item.quantity, 1)} size="small">
                          <Add />
                        </IconButton>
                        <IconButton onClick={() => handleRemoveItem(item.id)} color="error" size="small" sx={{ ml: 1 }}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < cartItems.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Pricing Error */}
      {pricingError && (
        <Alert severity="warning">{pricingError}</Alert>
      )}

      {/* Order Summary */}
      {cartItems.length > 0 && (
        <Card sx={{ backgroundColor: '#F8F9FA', border: '2px solid #E9ECEF' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="h5" sx={{ color: '#2C507A', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Order Summary
                </Typography>

                {appliedPromos.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {appliedPromos.map(p => (
                      <Chip key={p} label={p} size="small" color="success" variant="outlined" />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ color: '#6C757D' }}>Subtotal:</Typography>
                  <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>{formatPrice(totals.subtotalBefore)}</Typography>
                </Box>

                {totals.discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ color: '#6C757D' }}>Discount:</Typography>
                    <Typography sx={{ color: '#28a745', fontWeight: 600 }}>-{formatPrice(totals.discount)}</Typography>
                  </Box>
                )}

                {totals.bottleDepositTotal > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ color: '#6C757D' }}>Bottle Deposit:</Typography>
                    <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>{formatPrice(totals.bottleDepositTotal)}</Typography>
                  </Box>
                )}

                {totals.taxTotal > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ color: '#6C757D' }}>Tax:</Typography>
                    <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>{formatPrice(totals.taxTotal)}</Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography variant="h3" sx={{ color: '#2C507A', fontWeight: 700, mb: 1 }}>
                    {formatPrice(totals.grandTotal)}
                  </Typography>
                  <Typography sx={{ color: '#6C757D', fontSize: '1rem' }}>
                    Total
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          onClick={onBackToScanning}
          size="large"
          sx={{ minWidth: 150, ...ontarioTechStyles.contrast.whiteButton }}
        >
          Continue Shopping
        </Button>

        {cartItems.length > 0 && (
          <Button
            variant="contained"
            onClick={onProceedToPayment}
            size="large"
            startIcon={<Payment />}
            sx={{ minWidth: 200, backgroundColor: '#2C507A', '&:hover': { backgroundColor: '#1E3A5F' } }}
          >
            Proceed to Payment
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default CartInterface;
